import { Box, Group, Text, useMantineColorScheme, useMatches } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import classes from "../../UserProfile.module.css";

import { paths } from "@/config/paths";
import { useLogout } from "@/services/auth";

import type { FC } from "react";
import type { NavigationItem } from "../../types";

export type UserProfileSidebarBoxProps = {
  activeTab?: string;
  item: NavigationItem;
  onTabChange: (tab: string) => void;
};

export const UserProfileSidebarBox: FC<UserProfileSidebarBoxProps> = ({
  activeTab,
  item,
  onTabChange,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMatches({ base: true, sm: false });

  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";
  const isLogoutItem = item.label === "logout";

  const IconComponent = item.icon;
  const queryClient = useQueryClient();

  const { mutate: logoutMutate } = useLogout({
    onSuccess() {
      queryClient.removeQueries();
      navigate(paths.auth.login.getHref());
    },
  });

  function logout() {
    logoutMutate({});
  }

  if (isLogoutItem && isMobile) return null;

  return (
    <>
      <Box
        key={item.label}
        onClick={() => {
          if (isLogoutItem) {
            logout();
            return;
          }
          onTabChange(item.label);
        }}
        px="lg"
        py="md"
        style={{
          cursor: "pointer",
          userSelect: "none",
          borderRadius: "2px var(--mantine-radius-xs) var(--mantine-radius-xs) 2px",
          borderLeft: "3px solid",
          borderLeftColor:
            activeTab === item.label
              ? isDark
                ? "var(--mantine-color-primary-2)"
                : "var(--mantine-color-primary-6)"
              : "transparent",
          backgroundColor:
            activeTab === item.label
              ? isDark
                ? "var(--mantine-color-primaryDark-7)"
                : "var(--mantine-color-primary-0)"
              : "transparent",
          color:
            activeTab === item.label
              ? isDark
                ? "var(--mantine-color-primary-2)"
                : "var(--mantine-color-primary-7)"
              : "inherit",
          transition: "background-color 0.2s ease",
        }}
        onMouseEnter={(e) => {
          if (activeTab !== item.label) {
            e.currentTarget.style.backgroundColor = isDark
              ? "var(--mantine-color-dark-6)"
              : "var(--mantine-color-gray-1)";
          }
        }}
        onMouseLeave={(e) => {
          if (activeTab !== item.label) {
            e.currentTarget.style.backgroundColor = "transparent";
          }
        }}
      >
        <Group gap="sm" wrap="nowrap">
          <IconComponent
            style={{ minWidth: 18 }}
            size={18}
            stroke={1.5}
            className={isLogoutItem ? classes.redColorText : undefined}
          />
          <Text
            component="span"
            fz="md"
            className={isLogoutItem ? classes.redColorText : undefined}
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: "0.875em",
              fontWeight: activeTab === item.label ? 600 : 400,
            }}
          >
            {t(`profile.navLinks.${item.label}`)}
          </Text>
        </Group>
      </Box>
    </>
  );
};
